import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderMarkdownLite } from "../lib/renderMarkdown";

interface ChatMessage {
  role: "user" | "model";
  content: string;
  /** Which model actually produced this reply (gemini / gemma / guardrail). */
  model?: string;
  /** How the answer was sourced — drives the provenance badge in the UI. */
  provenance?: "faq" | "rag" | "live" | "guardrail";
  /** Source labels for the badge tooltip (e.g. "bible/sec05", "flood-prone-areas"). */
  sources?: string[];
}

/** Provenance badge label + colour — reuses the app's semantic data-state
    tokens rather than inventing new brand hues (Rams: one accent, no rainbow). */
function provenanceLabel(p: ChatMessage["provenance"]): { label: string; color: string; describe: string } | null {
  switch (p) {
    case "faq":       return { label: "FAQ",       color: "var(--good)", describe: "Answered from the curated FAQ cache — fast, pre-verified." };
    case "rag":       return { label: "KNOWLEDGE BASE", color: "var(--warn)", describe: "Answered by retrieving from the document knowledge base (bible, flood zones, operator docs)." };
    case "live":      return { label: "LIVE LLM",  color: "var(--info)", describe: "Answered by the live language model (Ollama gemma4:e4b locally, Gemini fallback)." };
    case "guardrail": return { label: "GUARDRAIL", color: "var(--bad)", describe: "Abuse pattern triggered a templated response." };
    default:          return null;
  }
}

interface Props {
  apiBase: string;
}

const SUGGESTED_PROMPTS = [
  "What's PM2.5 around campus right now and what should we tell students?",
  "Summarise KMITL's campus zones and the surrounding dormitory belt in 4 bullets.",
  "Which Bangkok open datasets matter most for campus operations?",
  "How busy is Chalong Krung Rd at 8:30 am on a weekday?",
  "How well-connected is campus via the ARL Lat Krabang station?",
  "Where would I find live CCTV near the Engineering faculty?",
];

const STORAGE_KEY = "kmitl:chat-history-v1";
const MAX_HISTORY_TURNS = 16;

function loadHistory(): ChatMessage[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatMessage[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_HISTORY_TURNS);
  } catch {
    return [];
  }
}

function saveHistory(messages: ChatMessage[]) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(messages.slice(-MAX_HISTORY_TURNS)),
    );
  } catch {}
}


export function ChatBox({ apiBase }: Props) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistory());
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => saveHistory(messages), [messages]);

  // Auto-scroll on new message / streaming reply
  useEffect(() => {
    if (!open) return;
    const t = transcriptRef.current;
    if (t) t.scrollTop = t.scrollHeight;
  }, [messages, busy, open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const busyRef = useRef(busy);
  useEffect(() => { busyRef.current = busy; }, [busy]);

  // Blocking fallback — used when the SSE endpoint is unavailable.
  const sendBlocking = useCallback(
    async (history: ChatMessage[], signal: AbortSignal) => {
      const res = await fetch(`${apiBase}/api/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages: history.slice(-MAX_HISTORY_TURNS).map(({ role, content }) => ({ role, content })) }),
        signal,
      });
      const json = (await res.json()) as { reply?: string; model?: string; error?: string };
      if (!res.ok || !json.reply) {
        setError(json.error ?? `Request failed (${res.status})`);
        return;
      }
      setMessages((prev) => [...prev, { role: "model", content: json.reply ?? "", model: json.model }]);
    },
    [apiBase],
  );

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || busyRef.current) return;
      setError(null);
      const newMessages: ChatMessage[] = [
        ...messages,
        { role: "user" as const, content: text },
      ];
      setMessages(newMessages);
      setInput("");
      setBusy(true);

      const ctrl = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ctrl;

      try {
        // Stream-first: tokens render as they arrive.
        const res = await fetch(`${apiBase}/api/chat/stream`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ messages: newMessages.slice(-MAX_HISTORY_TURNS).map(({ role, content }) => ({ role, content })) }),
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          await sendBlocking(newMessages, ctrl.signal);
          return;
        }

        // The reply lives at a fixed index (right after the user message),
        // and every updater is pure + idempotent — React StrictMode
        // double-invokes updaters, so closure mutation inside them
        // duplicates messages.
        const replyIndex = newMessages.length;
        let acc = "";
        let sawDelta = false;
        let streamError: string | null = null;
        const applyDelta = (t: string) => {
          acc += t;
          const next = acc;
          if (!sawDelta) {
            sawDelta = true;
            setMessages((prev) =>
              prev.length > replyIndex ? prev : [...prev, { role: "model" as const, content: "" }],
            );
          }
          setMessages((prev) =>
            prev.map((m, i) => (i === replyIndex ? { ...m, content: next } : m)),
          );
        };

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let sep: number;
          while ((sep = buf.indexOf("\n\n")) >= 0) {
            const rawEvent = buf.slice(0, sep);
            buf = buf.slice(sep + 2);
            let event = "message";
            let data = "";
            for (const line of rawEvent.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += line.slice(5).trim();
            }
            if (!data) continue;
            try {
              if (event === "delta") {
                applyDelta((JSON.parse(data) as { t?: string }).t ?? "");
              } else if (event === "done") {
                const meta = JSON.parse(data) as { model?: string; provenance?: ChatMessage["provenance"]; sources?: string[] };
                const finalContent = acc;
                setMessages((prev) =>
                  prev.map((m, i) =>
                    i === replyIndex
                      ? {
                          role: "model" as const,
                          content: finalContent,
                          model: meta.model,
                          provenance: meta.provenance,
                          sources: meta.sources,
                        }
                      : m,
                  ),
                );
              } else if (event === "error") {
                streamError = (JSON.parse(data) as { error?: string }).error ?? "Chat failed";
              }
            } catch { /* malformed event — skip */ }
          }
        }

        if (streamError && !sawDelta) {
          setError(streamError);
        } else if (!sawDelta) {
          // Stream completed without yielding anything (endpoint missing,
          // proxy stripped SSE) — fall back to the blocking endpoint.
          await sendBlocking(newMessages, ctrl.signal);
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          // Stream transport failed — try the blocking endpoint once.
          try {
            await sendBlocking(newMessages, abortRef.current?.signal as AbortSignal);
          } catch {
            setError("Network error — try again.");
          }
        }
      } finally {
        setBusy(false);
      }
    },
    [apiBase, busy, messages, sendBlocking],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      send(input);
    },
    [input, send],
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setError(null);
    setBusy(false);
  }, []);

  const turnCount = useMemo(() => messages.length, [messages]);

  // Header reflects whichever model actually answered last — surfaces the
  // Gemini → local-gemma fallback instead of a hardcoded label.
  const lastModel = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "model" && m.model && m.model !== "guardrail") return m.model;
    }
    return null;
  }, [messages]);

  return (
    <div className={`chatbox ${open ? "chatbox-open" : "chatbox-closed"}`}>
      {!open && (
        <button
          type="button"
          className="chat-handle"
          onClick={() => setOpen(true)}
          aria-label="Open KMITL concierge chat"
          title="KCT-Concierge — Concierge for KMITL campus · transit · datasets · ops"
        >
          <span className="chat-handle-dot" />
          {/* The collapsed launcher is a launcher, not a banner. It used to carry
              the sentence "Concierge for KMITL campus · transit · datasets · ops"
              inside a 520 px bar that sat on top of the left rail's gauge panel
              and the map legend — a short label inheriting a container-sized box
              and obscuring two live readings. The sentence is still there, as
              the tooltip; the box is now sized to its label. */}
          <span className="chat-handle-label">
            <strong>Ask KCT</strong>
          </span>
          <span className="chat-handle-cta mono">[ASK →]</span>
        </button>
      )}

      {open && (
        <div className="chat-panel" role="dialog" aria-label="KMITL concierge chat">
          <header className="chat-head">
            <div className="col">
              <span className="eyebrow mono">KCT-Concierge · {lastModel ?? "gemma4:e4b"}</span>
              <h2 className="chat-title">Ask anything about KMITL</h2>
            </div>
            <div className="chat-head-tools">
              {turnCount > 0 && (
                <button onClick={clear} className="mono chat-clear" aria-label="Clear conversation">
                  CLEAR
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="mono chat-close"
                aria-label="Close chat"
              >
                [ESC] CLOSE
              </button>
            </div>
          </header>

          <div ref={transcriptRef} className="chat-transcript">
            {messages.length === 0 && (
              <div className="chat-empty">
                <p className="chat-empty-lede">
                  Local Gemma 4 · self-hosted · privacy-first. Grounded in live
                  campus data — AQ, incidents, weather, transit. Try:
                </p>
                <div className="chat-suggestions">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="chat-suggestion"
                      onClick={() => send(p)}
                      disabled={busy}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg-${m.role}`}>
                <span className="chat-msg-role mono">
                  {m.role === "user" ? "YOU" : "KCT"}
                  {m.role === "model" && m.model && m.model !== "guardrail" && (
                    <span className="chat-msg-model"> · {m.model}</span>
                  )}
                  {/* Provenance badge — tells the user where the answer came from. */}
                  {m.role === "model" && (() => {
                    const p = provenanceLabel(m.provenance);
                    if (!p) return null;
                    const sourcesLine = m.sources && m.sources.length > 0
                      ? `\nSources: ${m.sources.slice(0, 4).join(", ")}`
                      : "";
                    return (
                      <span
                        className="chat-provenance mono"
                        style={{ color: p.color, borderColor: p.color }}
                        title={p.describe + sourcesLine}
                        aria-label={p.describe}
                      >
                        {p.label}
                      </span>
                    );
                  })()}
                  {m.role === "model" && m.content && (
                    <button
                      className="chat-copy-btn mono"
                      aria-label="Copy response"
                      title="Copy to clipboard"
                      onClick={() => navigator.clipboard.writeText(m.content).catch(() => {})}
                    >
                      COPY
                    </button>
                  )}
                </span>
                <div className="chat-msg-body">
                  {m.role === "model" ? renderMarkdownLite(m.content) : <p>{m.content}</p>}
                </div>
              </div>
            ))}

            {busy && (
              <div className="chat-msg chat-msg-model chat-msg-busy">
                <span className="chat-msg-role mono">KCT</span>
                <div className="chat-msg-body">
                  <span className="chat-typing">
                    <span /><span /><span />
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="chat-error mono" role="alert">
                {error}
              </div>
            )}
          </div>

          <form className="chat-input" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about campus traffic, transit, air quality, datasets…"
              maxLength={1200}
              disabled={busy}
              aria-label="Your message"
              autoFocus
            />
            <button type="submit" className="mono" disabled={busy || !input.trim()}>
              {busy ? "…" : "SEND"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
